import React, { useState, useEffect } from "react";
import { __ } from "@wordpress/i18n";
import Constants from "../constants";

const Filters = ({
	filtersData,
	setFiltersData,
	onApplyFilters,
	jobTypes,
	phrases,
}) => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState(filtersData.Keyword);

	// Handle debounced search
	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			if (searchTerm !== filtersData.Keyword) {
				setFiltersData({
					...filtersData,
					Keyword: searchTerm,
				});
				onApplyFilters();
			}
		}, 500); // 500ms delay

		return () => clearTimeout(delayDebounceFn);
	}, [searchTerm]);

	const handleKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			setFiltersData({
				...filtersData,
				Keyword: searchTerm,
			});
			onApplyFilters();
		}
	};

	const handleOnApplyFilters = () => {
		onApplyFilters();
		setIsPopupOpen(false);
	};

	const handleClearFilters = () => {
		setFiltersData({
			Keyword: "",
			SortBy: "",
			DatePosted: "",
			SortOrder: "",
			VolunteerDuration: "",
		});
		setSearchTerm("");
		onApplyFilters();
		setIsPopupOpen(false);
	};

	return (
		<>
			<div className="relative flex items-center justify-between filter-controls">
				<div className="flex items-center">
					<button
						className="me-5 h-12 px-5 rtl:flex-row-reverse border-0 py-1.5 transition focus:ring-2 focus:ring-inset appearance-none bg-lightGray bg-no-repeat rounded-lg justify-center items-center text-body text-sm font-medium gap-3.5 inline-flex"
						onClick={(e) => setIsPopupOpen(true)}
					>
						{__(phrases.filters, Constants.TEXT_DOMAIN)}
						<svg
							width="20"
							height="20"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M6 3.25C3.932 3.25 2.25 4.932 2.25 7C2.25 9.068 3.932 10.75 6 10.75C8.068 10.75 9.75 9.068 9.75 7C9.75 4.932 8.068 3.25 6 3.25ZM6 9.25C4.759 9.25 3.75 8.241 3.75 7C3.75 5.759 4.759 4.75 6 4.75C7.241 4.75 8.25 5.759 8.25 7C8.25 8.241 7.241 9.25 6 9.25ZM18 13.25C15.932 13.25 14.25 14.932 14.25 17C14.25 19.068 15.932 20.75 18 20.75C20.068 20.75 21.75 19.068 21.75 17C21.75 14.932 20.068 13.25 18 13.25ZM18 19.25C16.759 19.25 15.75 18.241 15.75 17C15.75 15.759 16.759 14.75 18 14.75C19.241 14.75 20.25 15.759 20.25 17C20.25 18.241 19.241 19.25 18 19.25ZM15 7.75H21C21.414 7.75 21.75 7.414 21.75 7C21.75 6.586 21.414 6.25 21 6.25H15C14.586 6.25 14.25 6.586 14.25 7C14.25 7.414 14.586 7.75 15 7.75ZM9 16.25H3C2.586 16.25 2.25 16.586 2.25 17C2.25 17.414 2.586 17.75 3 17.75H9C9.414 17.75 9.75 17.414 9.75 17C9.75 16.586 9.414 16.25 9 16.25Z"
								fill="#F67748"
							/>
						</svg>
					</button>
					<select
						className="sortBySelector h-12 ltr:pe-6 ltr:ps-5 rtl:pe-5 rtl:ps-10 border-0 py-1.5 transition focus:ring-2 focus:ring-inset appearance-none bg-lightGray bg-no-repeat rounded-lg justify-center items-center text-body text-sm font-medium gap-3.5 inline-flex"
						onChange={(e) =>
							setFiltersData({
								...filtersData,
								SortBy: e.target.value,
							})
						}
						value={filtersData.SortBy}
					>
						<option value="">
							{__(phrases.sort_by, Constants.TEXT_DOMAIN)}
						</option>
						<option value="datePosted">
							{__(phrases.date_posted, Constants.TEXT_DOMAIN)}
						</option>
						<option value="volunteerDuration">
							{__(phrases.volunteer_duration, Constants.TEXT_DOMAIN)}
						</option>
					</select>
				</div>
				<input
					type="text"
					placeholder={__(phrases.search_volunteers, Constants.TEXT_DOMAIN)}
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					onKeyDown={handleKeyDown}
					className="block w-full h-12 max-w-sm px-5 py-1 transition border-0 rounded-md bg-lightGray text-body focus:ring-2 focus:ring-inset sm:leading-6"
				/>

				{isPopupOpen && (
					<div className="absolute top-0 w-full max-w-sm p-6 bg-white rounded-lg shadow-md ltr:left-0 rtl:right-0">
						<button
							className="absolute text-2xl text-gray-500 transition-all top-4 rtl:left-4 ltr:right-4 ms-5 hover:text-red-600 p-2"
							aria-label="Close"
							onClick={() => {
								setIsPopupOpen(false);
							}}
						>
							<i className="fa-solid fa-xmark"></i>
						</button>

						<h2 className="mb-6 text-xl font-bold text-gray-800">
							{__(phrases.filters, Constants.TEXT_DOMAIN)}
						</h2>

						<div className="space-y-4">
							{/* Date Posted Filter */}
							<div>
								<label className="block mb-2 font-semibold text-gray-700">
									{__(phrases.date_posted, Constants.TEXT_DOMAIN)}
								</label>
								<div className="space-y-2">
									<input
										type="date"
										className="w-full p-2 text-gray-600 border rounded-lg focus:ring-primary focus:border-primary"
										value={filtersData.DatePosted || ""}
										onChange={(e) =>
											setFiltersData({
												...filtersData,
												DatePosted: e.target.value,
											})
										}
									/>
								</div>
							</div>

							<div className="mt-6 flex justify-between">
								<button
									onClick={handleClearFilters}
									className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
								>
									{__(
										phrases.clear_filters || "Clear Filters",
										Constants.TEXT_DOMAIN,
									)}
								</button>
								<button
									onClick={handleOnApplyFilters}
									className="px-4 py-2 text-white bg-primary rounded hover:bg-darkPrimary transition-colors"
								>
									{__(phrases.apply_filters, Constants.TEXT_DOMAIN)}
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default Filters;
